/**
 * What the session's role may do on this screen.
 *
 * ONE predicate, `mayWrite` from `@plat/core/roles`, fed by the role the session route reported —
 * the same table the API gate enforces, imported, not copied. A screen asks `useGrant(path)` for
 * the form it would render; when the answer is no it renders `<NeedsRole>` in the form's place: one
 * line that says what the grant is and which role could change this. The console never refuses on
 * its own — the API's 403 is the control and holds whatever is rendered — it just stops showing a
 * form that could only ever fail on submit.
 */

import { mayCall, minimumRole, normalizeRole, type Role } from "@plat/core/roles";
import { useSession } from "./session";

export interface Grant {
  /** The session's role, normalised: a role outside the set, or no session, is VIEWER. */
  role: Role;
  /** May this role POST to `path`? */
  allowed: boolean;
  /** The lowest role that may. */
  need: Role;
}

/** The grant for one write path, from the session. Pure apart from reading the session context. */
export function useGrant(path: string, method = "POST"): Grant {
  const { session } = useSession();
  return grantFor(session?.signedIn ? session.role : undefined, path, method);
}

export function grantFor(rawRole: string | undefined, path: string, method = "POST"): Grant {
  const role = normalizeRole(rawRole);
  return { role, allowed: mayCall(role, method, path), need: minimumRole(method, path) };
}

/** The one honest line: "Your grant is VIEWER; an ADMIN can change this." */
export function grantLine(role: Role, need: Role): string {
  const article = /^[AEIOU]/.test(need) ? "an" : "a";
  return `Your grant is ${role}; ${article} ${need} can change this.`;
}

/** Rendered where a form would be, when the session's role could not submit it. */
export function NeedsRole({ path, method = "POST" }: { path: string; method?: string }) {
  const { role, need } = useGrant(path, method);
  return (
    <p className="muted" data-testid="needs-role" data-path={path} style={{ marginTop: 8 }}>
      {grantLine(role, need)}
    </p>
  );
}
